//#region Imports
import { useState } from 'react';
import { faTimes } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';

import { GlobalVariables } from '../../../../globalVar';
import customAxios from '../../../../utils/customAxios';
import { getToast } from '../../../../utils/toastConfig';
//#endregion

export const CreateFailureModal = ({
  failure_type_id,
  showModal,
  setShowModal,
  refreshData,
}) => {
  const [isLoading, setIsLoading] = useState(false); // State for tracking loading state
  const [newFailure, setNewFailure] = useState({
    failure_type_id,
    name: '',
    level: '',
    sla_hours: '',
    description: '',
    notes: '',
  }); // State for new failure data

  const levels = ['1', '2', '3', '4', '5'];
  const hours = ['1', '2', '3', '4', '6', '8', '9', '12', '24', '48', '72', '168'];

  /**
   * Resets the fields in the form to their initial state.
   */
  const handleResetFields = () => {
    setNewFailure({
      failure_type_id,
      name: '',
      level: '',
      sla_hours: '',
      description: '',
      notes: '',
    });
  };

  /**
   * Closes the modal and resets the form fields.
   */
  const handleClose = () => {
    setShowModal(false);
    handleResetFields();
    refreshData(); // Refresh failure types data
  };

  /**
   * Handles the form submission to create a new failure type.
   * @param {Event} e - The form submission event.
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    const endpoint = `${GlobalVariables.getURL()}failures/create`;

    try {
      const response = await customAxios.post(endpoint, newFailure, {
        headers: {
          Authorization: 'bearer ' + localStorage.getItem('token'),
        },
      });

      if (response.status === 200) {
        getToast({
          status: 'success',
          title: 'Falla creada exitosamente',
        }); // Show success message
        handleClose(); // Close modal and reset fields
      }
    } catch (error) {
      console.error('Error creating failure:', error);
      getToast({
        status: 'error',
        title: error.response?.data?.message || 'Error',
      }); // Show error message
    } finally {
      setIsLoading(false); // Reset loading state
    }
  };

  return (
    <Dialog open={showModal} maxWidth='md' fullWidth onClose={handleClose}>
      <DialogTitle>
        <Typography className='inventory-movmodal-title'>
          Nueva falla
        </Typography>
      </DialogTitle>
      <IconButton
        aria-label='close'
        onClick={handleClose}
        sx={{
          position: 'absolute',
          right: 20,
          top: 20,
          width: 40,
          height: 40,
        }}
      >
        <FontAwesomeIcon icon={faTimes} />
      </IconButton>
      <DialogContent dividers>
        <form id='new-region-form' onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            {/* Name Field */}
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                disabled={isLoading}
                label='Nombre falla SN'
                placeholder='Nombre falla SN...'
                type='text'
                value={newFailure.name}
                onChange={(e) =>
                  setNewFailure((prev) => ({
                    ...prev,
                    name: e.target.value,
                  }))
                }
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth required>
                <InputLabel id='select-level-label'>
                  Nivel de la falla
                </InputLabel>
                <Select
                  labelId='select-level-label'
                  value={newFailure.level}
                  label='Nivel de la falla'
                  placeholder='Nivel de la falla...'
                  onChange={(e) =>
                    setNewFailure((prev) => ({
                      ...prev,
                      level: e.target.value,
                    }))
                  }
                >
                  {levels.map((level) => (
                    <MenuItem key={level} value={level}>
                      {level}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth required>
                <InputLabel id='select-hours-label'>Horas</InputLabel>
                <Select
                  labelId='select-hours-label'
                  value={newFailure.sla_hours}
                  label='Horas'
                  placeholder='Horas...'
                  onChange={(e) =>
                    setNewFailure((prev) => ({
                      ...prev,
                      sla_hours: e.target.value,
                    }))
                  }
                >
                  {hours.map((hours) => (
                    <MenuItem key={hours} value={hours}>
                      {hours}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {/* Description Field */}
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                disabled={isLoading}
                label='Descripción de la falla'
                placeholder='Descripción de la falla...'
                multiline
                minRows={3}
                maxRows={3}
                type='text'
                value={newFailure.description}
                onChange={(e) =>
                  setNewFailure((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
              />
            </Grid>
            {/* Notes Field */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                disabled={isLoading}
                label='Notas'
                placeholder='Notas...'
                type='text'
                value={newFailure.notes}
                onChange={(e) =>
                  setNewFailure((prev) => ({
                    ...prev,
                    notes: e.target.value,
                  }))
                }
              />
            </Grid>
          </Grid>
        </form>
      </DialogContent>
      <DialogActions>
        <Button
          variant='contained'
          form='new-region-form'
          type='submit'
          disabled={isLoading}
        >
          {isLoading ? (
            <CircularProgress size='2rem' />
          ) : (
            <Typography>Guardar</Typography>
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
